/** @format */

import { flushSync } from 'react-dom';

export type LocationKeyParts = {
  pathname: string;
  search: string;
  hash: string;
  key?: string;
};

export type RouteDisplayUpdateType = 'none' | 'sync' | 'transition';

export function getLocationKey(location: LocationKeyParts): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

function hasLocationIdentityChanged(live: LocationKeyParts, display: LocationKeyParts): boolean {
  return (live.key ?? '') !== (display.key ?? '');
}

/** Decide how to apply a router location update for view transitions. */
export function getRouteDisplayUpdateType(
  liveSwitch: LocationKeyParts,
  liveCurrent: LocationKeyParts,
  displaySwitch: LocationKeyParts,
  displayCurrent: LocationKeyParts
): RouteDisplayUpdateType {
  const switchChanged = getLocationKey(liveSwitch) !== getLocationKey(displaySwitch);
  const currentChanged = getLocationKey(liveCurrent) !== getLocationKey(displayCurrent);

  if (!switchChanged && !currentChanged) {
    // Same URL but new history entry (e.g. replace clearing resetExtranetTopicNav)
    if (
      hasLocationIdentityChanged(liveSwitch, displaySwitch) ||
      hasLocationIdentityChanged(liveCurrent, displayCurrent)
    ) {
      return 'sync';
    }
    return 'none';
  }

  // Main view unchanged (e.g. extranet drawer open/close) — update without VT
  if (!switchChanged) {
    return 'sync';
  }

  return 'transition';
}

export function startViewTransition(callback: () => void): { finished: Promise<void> } | null {
  if (typeof document !== 'undefined' && typeof document.startViewTransition === 'function') {
    const transition = document.startViewTransition(() => {
      flushSync(callback);
    });
    transition.finished.catch(() => {});
    return transition;
  }

  callback();
  return null;
}
